import { useNavigate, useParams, Link } from 'react-router-dom';
import { Trash2, Edit } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useUserInfo, useDeleteUser } from '@/api/hooks/useUsers';
import { ApiException } from '@/api/client';

export function UserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const decodedId = decodeURIComponent(id);
  const navigate = useNavigate();
  const { data, isLoading, error } = useUserInfo(decodedId);
  const deleteUser = useDeleteUser();

  if (isLoading) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  if (error || !data) {
    return (
      <Card>
        <div style={{ padding: 24, color: '#991b1b', fontSize: 13 }}>
          <strong>Failed to load user:</strong>{' '}
          {error instanceof ApiException ? error.message : 'Unknown error'}
          <div style={{ marginTop: 12 }}>
            <Button variant="outline" onClick={() => navigate('/users')}>
              Back to Users
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const handleDelete = () => {
    if (confirm(`Delete user ${data.user_email ?? data.user_id}?`)) {
      deleteUser.mutate([data.user_id], {
        onSuccess: () => navigate('/users'),
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
          {data.user_email ?? data.user_id}
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to={`/users/${encodeURIComponent(data.user_id)}/edit`}>
            <Button variant="outline">
              <Edit size={16} />
              <span>Edit</span>
            </Button>
          </Link>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 size={16} />
            <span>Delete</span>
          </Button>
        </div>
      </div>
      <Card>
        <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Row label="User ID" value={data.user_id} />
          <Row label="Email" value={data.user_email ?? '-'} />
          <Row label="Role" value={data.user_role ?? '-'} />
          <Row label="Teams" value={data.teams?.join(', ') ?? '-'} />
          <Row label="Spend" value={`$${(data.spend ?? 0).toFixed(2)}`} />
          <Row
            label="Max Budget"
            value={data.max_budget != null ? `$${data.max_budget.toFixed(2)}` : '-'}
          />
          <Row label="Keys" value={data.key_count ?? '-'} />
          <Row label="Created" value={data.created_at ?? '-'} />
        </dl>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: '#64748b', fontSize: 13 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 14, color: '#0f172a' }}>{value}</dd>
    </>
  );
}